import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { OptimisationBarLayoutDto } from '@cutlyft/zod-schemas';

interface BarLayoutChartProps {
  barLayouts: OptimisationBarLayoutDto[];
}

interface ChartRow {
  name: string;
  cuts: number;
  waste: number;
  offcut: number;
}

function buildChartData(barLayouts: OptimisationBarLayoutDto[]): ChartRow[] {
  return barLayouts.map((bl) => ({
    name: `Bar ${bl.barIndex + 1}`,
    cuts: bl.totalCutLengthMm,
    waste: bl.wasteMm,
    offcut: bl.offcutReturnedMm,
  }));
}

const FILL = {
  cuts: 'hsl(var(--primary))',
  waste: 'hsl(var(--destructive))',
  offcut: 'hsl(var(--muted-foreground))',
};

function mmLabel(value: number) {
  return value >= 1000 ? `${(value / 1000).toFixed(1)} m` : `${value} mm`;
}

export function BarLayoutChart({ barLayouts }: BarLayoutChartProps) {
  const data = buildChartData(barLayouts);

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, barLayouts.length * 36 + 60)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 24, bottom: 8, left: 56 }}
      >
        <XAxis
          type="number"
          tickFormatter={mmLabel}
          tick={{ fontSize: 11 }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={52}
          tick={{ fontSize: 11 }}
        />
        <Tooltip formatter={(value) => typeof value === 'number' ? mmLabel(value) : String(value)} />
        <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="cuts" name="Cuts" stackId="a" fill={FILL.cuts} />
        <Bar dataKey="offcut" name="Offcut" stackId="a" fill={FILL.offcut} />
        <Bar dataKey="waste" name="Waste" stackId="a" fill={FILL.waste} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
